import { useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { failureOf } from '@/api/errors';
import { useAvailability, useContacts, useCost, useHold, usePipeline, useSlots, type HoldAttendees, type HoldResult, type WirePipeline } from '@/api/hooks';
import { useMeOrThrow } from '@/app/MeContext';
import type { WireContact, WireSlot } from '@/api/wire';
import { useDepartmentLabel } from '@/app/departments';
import { PageHeader, useFrameMaybe } from '@/app/frame';
import { clock, twinGiven } from '@/today/copy';
import { AttendeePicker, InviteStep, NOBODY } from '@/today/Attendees';
import { usd } from '@/ui/meter';
import { Button, EmptyState, Skeleton, TextInput, TextAction } from '@/ui/primitives';
import { Refusal } from '@/ui/Refusal';
import { Explainer, type ItemContext } from '@/ui/context';
import { useWho } from '@/ui/who';
import { viewerOf } from '@/directory/explainKit';
import { explainContact } from '@/directory/explain';
import { explainPipelineStatus, explainSpend, explainStretch } from './explain';
import { useToast } from '@/ui/Toast';

/**
 * Insight views (P:767-793): one question, one sentence answering it, and a
 * bar per thing it is about. Every number is read from what the platform
 * already answers — the cost ledger, the directory's last-contact columns,
 * the person's own bookable slots — and nothing is estimated here.
 */

export const INSIGHTS = [
  { key: 'spend', title: 'AI spend, last fortnight' },
  { key: 'quiet', title: 'Gone quiet' },
  { key: 'slots', title: 'Your next open slots' },
  { key: 'pipeline', title: 'Pipeline by status' },
] as const;

export type InsightKey = (typeof INSIGHTS)[number]['key'];

export function Insight() {
  const { key } = useParams<{ key: string }>();
  switch (key) {
    case 'spend':
      return <Spend />;
    case 'quiet':
      return <Quiet />;
    case 'slots':
      return <Slots />;
    case 'pipeline':
      return <Pipeline />;
    default:
      return <Unknown asked={key ?? ''} />;
  }
}

// --- the frame every view shares ------------------------------------------------

interface Row {
  id: string;
  label: string;
  /** 0-100. */
  pct: number;
  bar: 'accent' | 'warning' | 'quiet';
  value: string;
  action?: { label: string; onClick: () => void; disabled?: boolean } | undefined;
  /** What this row is, who it concerns and what to do about it (ADR-0155). */
  explain?: ItemContext | undefined;
}

const BAR: Record<Row['bar'], string> = {
  accent: 'bg-accent',
  warning: 'bg-warning',
  quiet: 'bg-control',
};

function View({
  title,
  lead,
  rows,
  footer,
  notes,
  children,
}: {
  title: string;
  lead: string;
  rows: Row[];
  footer?: Array<{ label: string; onClick: () => void; primary?: boolean }> | undefined;
  notes?: string[] | undefined;
  children?: ReactNode;
}) {
  return (
    <Page title={title}>
      <p className="m-0 max-w-[66ch] text-readback leading-relaxed" data-testid="insight-lead">
        {lead}
      </p>
      {rows.length > 0 && (
        <ul aria-label={title} className="m-0 flex list-none flex-col gap-px border border-line bg-line p-0">
          {rows.map((r) => (
            <li
              key={r.id}
              data-testid="insight-row"
              className="grid min-h-tap grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 gap-y-2 bg-page px-4 py-[13px] md:grid-cols-[minmax(0,180px)_minmax(0,1fr)_minmax(0,110px)_auto]"
            >
              <span className="min-w-0 break-words text-dense font-semibold">{r.label}</span>
              <span
                className="relative col-span-2 row-start-2 block h-2.5 bg-sunken md:col-span-1 md:row-start-auto"
                role="meter"
                aria-label={`${r.label}: ${r.value}`}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={r.pct}
              >
                <span
                  data-testid="insight-bar"
                  className={`absolute inset-y-0 left-0 block ${BAR[r.bar]}`}
                  style={{ width: `${r.pct}%` }}
                />
              </span>
              <span className="col-start-2 row-start-1 text-right text-dense tabular-nums md:col-start-auto md:row-start-auto">
                {r.value}
              </span>
              {r.action ? (
                <span className="col-span-2 justify-self-start md:col-span-1 md:justify-self-end">
                  <Button size="row" variant="link" className="px-0 md:px-3.5" disabled={r.action.disabled} onClick={r.action.onClick}>
                    {r.action.label}
                  </Button>
                </span>
              ) : (
                <span className="hidden md:block" />
              )}
              {r.explain && (
                <span className="col-span-2 md:col-span-4">
                  <Explainer context={r.explain} />
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
      {children}
      {footer && footer.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {footer.map((f) => (
            <Button key={f.label} variant={f.primary ? 'primary' : 'quiet'} onClick={f.onClick}>
              {f.label}
            </Button>
          ))}
        </div>
      )}
      {notes && notes.length > 0 && (
        <div className="flex flex-col gap-2">
          {notes.map((n) => (
            <span key={n} className="flex gap-2.5 text-dense leading-normal">
              <span aria-hidden className="shrink-0 text-accent">
                —
              </span>
              <span>{n}</span>
            </span>
          ))}
        </div>
      )}
    </Page>
  );
}

function Page({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="mx-auto flex w-full max-w-[1040px] flex-col gap-6 p-6 phone:p-4">
      <PageHeader kicker="Insight" title={title} />
      {children}
    </div>
  );
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

// --- spend ----------------------------------------------------------------------

function Spend() {
  const title = 'AI spend, last fortnight';
  const cost = useCost(14);
  const label = useDepartmentLabel();
  const navigate = useNavigate();
  const who = useWho();
  if (cost.isPending) return <Page title={title}><Skeleton rows={4} /></Page>;
  if (cost.isError) return <Page title={title}><Refusal failure={failureOf(cost.error)} /></Page>;

  const departments = [...cost.data.byDepartment].sort((a, b) => b.spentUsd - a.spentUsd);
  const max = Math.max(...departments.map((d) => d.spentUsd), 0);
  const top = departments[0];
  const idle = departments.filter((d) => d.spentUsd > 0 && d.shipped === 0);

  let lead = `${usd(cost.data.totalUsd)} across ${plural(departments.length, 'department')} in ${plural(cost.data.calls, 'call')}.`;
  if (departments.length === 0 || max === 0) lead = 'Nothing has been spent on models in the last fortnight.';
  else if (top && top.shipped > 0 && top.perChangeUsd !== null) {
    lead += ` ${label(top.department)} spends the most and landed ${plural(top.shipped, 'change')} — ${usd(top.perChangeUsd)} per change.`;
  } else if (top) {
    lead += ` ${label(top.department)} spends the most.`;
  }

  const notes = [
    'Every number is the cost ledger’s, written as each call was made. Nothing here is estimated.',
    ...idle.map((d) => `${label(d.department)} spent ${usd(d.spentUsd)} and shipped nothing in the window — worth a conversation, not an alarm.`),
  ];

  return (
    <View
      title={title}
      lead={lead}
      rows={departments.map((d, rank) => ({
        id: d.department,
        explain: explainSpend(d, cost.data, rank, who),
        label: label(d.department),
        // A department that spent anything shows at least a sliver; one that spent nothing shows none.
        pct: max > 0 && d.spentUsd > 0 ? Math.max(2, Math.round((d.spentUsd / max) * 100)) : 0,
        bar: d.spentUsd > 0 && d.shipped === 0 ? 'warning' : 'accent',
        value: usd(d.spentUsd),
        action: { label: 'Open', onClick: () => navigate(`/departments/${encodeURIComponent(d.department)}`) },
      }))}
      footer={[{ label: 'Open reports', onClick: () => navigate('/reports') }]}
      notes={notes}
    />
  );
}

// --- pipeline -----------------------------------------------------------------------

/**
 * Pipeline by status (P:2100): the department's own deals counted and summed
 * per currency — never converted, since a single total would need a rate
 * nobody has decided — then what has stopped moving and where deals are
 * lost. The backend refuses where there are no deals; that is shown as what
 * it is, not as an empty chart.
 */
const money = (totals: WirePipeline['statuses'][number]['totals']) =>
  totals.length === 0
    ? '—'
    : totals
        .slice()
        .sort((a, b) => a.currency.localeCompare(b.currency))
        .map((t) => `${Number(t.total).toLocaleString('en-GB', { maximumFractionDigits: 0 })} ${t.currency}`)
        .join(' · ');

/** Totals of several statuses, added per currency and never across one. */
function sumByCurrency(totals: WirePipeline['statuses'][number]['totals']) {
  const by = new Map<string, number>();
  for (const t of totals) by.set(t.currency, (by.get(t.currency) ?? 0) + Number(t.total));
  return [...by].map(([currency, total]) => ({ currency, total: String(total) }));
}

function Pipeline() {
  const title = 'Pipeline by status';
  const me = useMeOrThrow();
  const label = useDepartmentLabel();
  const pipeline = usePipeline(me.department);
  const navigate = useNavigate();
  const who = useWho();
  if (pipeline.isPending) return <Page title={title}><Skeleton rows={4} /></Page>;
  if (pipeline.isError) {
    const failure = failureOf(pipeline.error);
    if (failure.kind === 'refused' && failure.code === 'no_pipeline') {
      return (
        <Page title={title}>
          <EmptyState
            title={`${label(me.department)} keeps no deals`}
            body="A pipeline is a department’s own Deal records, and this department has none. Switch to a department that keeps them to see its pipeline — another department’s is never shown here."
          />
        </Page>
      );
    }
    return <Page title={title}><Refusal failure={failure} /></Page>;
  }
  const p = pipeline.data;
  const openish = p.statuses.filter((s) => s.status !== 'won' && s.status !== 'lost');
  const openCount = openish.reduce((n, s) => n + s.count, 0);
  const won = p.statuses.find((s) => s.status === 'won');
  const max = Math.max(...p.statuses.map((s) => s.count), 0);
  const lead =
    p.statuses.length === 0
      ? `${label(me.department)} has no deals recorded yet.`
      : `${plural(openCount, 'open deal')} worth ${money(sumByCurrency(openish.flatMap((s) => s.totals)))}.${won ? ` ${plural(won.count, 'deal')} won, worth ${money(won.totals)}.` : ''}`;
  const notes = [
    'Every number is a count or a sum of this department’s own Deal records. Totals are per currency and never converted.',
    ...(p.stale.length > 0
      ? [`${plural(p.stale.length, 'open deal')} ${p.stale.length === 1 ? 'has' : 'have'} not moved in ${p.staleAfterDays} days: ${p.stale
          .slice(0, 3)
          .map((d) => `${d.title ?? 'a deal'}${d.company ? ` (${d.company})` : ''}${d.value ? `, ${Number(d.value).toLocaleString('en-GB')} ${d.currency ?? ''}`.trimEnd() : ''}`)
          .join('; ')}.`]
      : []),
    ...(p.lost.length > 0
      ? [`Lost deals cluster at ${p.lost
          .slice(0, 3)
          .map((l) => `${l.company ?? 'no company'} (${l.count})`)
          .join(', ')}.`]
      : []),
    // Why, where the live Deal asks (deal_pipeline v2). The reasons are the
    // words people gave when they marked a deal lost; one with none shows as
    // a gap, not as nothing.
    ...(p.lostWhy && p.lostWhy.length > 0
      ? [`Deals were lost because of ${p.lostWhy
          .slice(0, 4)
          .map((r) => `${r.reason ?? 'no reason given'} (${r.count})`)
          .join(', ')}.`]
      : []),
  ];
  return (
    <View
      title={title}
      lead={lead}
      rows={p.statuses.map((s) => ({
        id: s.status,
        explain: explainPipelineStatus(s, p, who, viewerOf(me), me.department),
        label: s.status.charAt(0).toUpperCase() + s.status.slice(1),
        pct: max > 0 && s.count > 0 ? Math.max(2, Math.round((s.count / max) * 100)) : 0,
        bar: s.status === 'lost' || s.status === 'stalled' ? 'warning' : s.status === 'won' ? 'accent' : 'quiet',
        value: `${s.count} · ${money(s.totals)}`,
      }))}
      footer={[{ label: 'Open the deals', onClick: () => navigate('/records/Deal') }]}
      notes={notes}
    />
  );
}

// --- gone quiet -------------------------------------------------------------------

const QUIET_DAYS = 30;
const DAY = 86_400_000;

function lastTouched(c: WireContact): number | null {
  const at = [c.lastInboundAt, c.lastOutboundAt].filter((x): x is string => !!x).map((x) => new Date(x).getTime());
  return at.length ? Math.max(...at) : null;
}

const longDate = (ms: number) =>
  new Date(ms).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

function Quiet() {
  const title = 'Gone quiet';
  const contacts = useContacts('');
  const frame = useFrameMaybe();
  const who = useWho();
  const me = useMeOrThrow();
  const now = useMemo(() => Date.now(), []);
  if (contacts.isPending) return <Page title={title}><Skeleton rows={5} /></Page>;
  if (contacts.isError) return <Page title={title}><Refusal failure={failureOf(contacts.error)} /></Page>;

  const quiet = contacts.data.contacts
    .map((c) => ({ c, at: lastTouched(c) }))
    // Never written to is quiet only once they have been on the list a while:
    // somebody added yesterday has not gone anywhere.
    .filter(({ c, at }) => (at === null ? now - new Date(c.createdAt).getTime() >= QUIET_DAYS * DAY : now - at >= QUIET_DAYS * DAY))
    .sort((a, b) => (a.at ?? 0) - (b.at ?? 0));

  if (quiet.length === 0) {
    return (
      <Page title={title}>
        <EmptyState
          title="Nobody has gone quiet"
          body={`Everyone in the directory has written or been written to in the last ${QUIET_DAYS} days.`}
        />
      </Page>
    );
  }

  const oldest = Math.max(...quiet.map(({ at }) => (at === null ? 0 : now - at)), 1);
  const twin = frame?.twin ?? null;
  const notes = [
    'Last contact is the latest email in or out that the platform saw. A call or a meeting it was not told about does not count.',
  ];
  // The directory answers a hundred at a time; say so rather than imply the rest are fine.
  if (contacts.data.contacts.length >= 100) notes.push(`Only the first ${contacts.data.contacts.length} people in the directory are counted.`);

  return (
    <View
      title={title}
      lead={`${plural(quiet.length, 'person', 'people')} with no email in or out for over ${QUIET_DAYS} days.`}
      rows={quiet.map(({ c, at }) => {
        const since = at === null ? null : longDate(at);
        return {
          id: c.id,
          explain: explainContact(c, who, { ...viewerOf(me), now }),
          label: c.fullName,
          pct: at === null ? 100 : Math.max(2, Math.round(((now - at) / oldest) * 100)),
          bar: 'quiet' as const,
          value: at === null ? 'never' : `${Math.floor((now - at) / DAY)} days`,
          action: {
            label: twin ? `Ask ${twin} to draft a note` : 'Draft a note',
            disabled: !frame,
            onClick: () =>
              frame?.ask(
                since === null
                  ? `Draft a short note to ${c.fullName} — we have not been in touch yet.`
                  : `Draft a short note to ${c.fullName} — we have not been in touch since ${since}.`,
              ),
          },
        };
      })}
      notes={notes}
    />
  );
}

// --- open slots -------------------------------------------------------------------

interface Stretch {
  from: string;
  to: string;
  minutes: number;
}

/**
 * The slots endpoint answers back-to-back half-hours. Contiguous ones are
 * one free stretch, and "longest first" is about stretches: a free morning
 * is worth more to offer than four scattered half-hours.
 */
function windowsOf(slots: WireSlot[]): Stretch[] {
  const sorted = [...slots].sort((a, b) => a.from.localeCompare(b.from));
  const out: Stretch[] = [];
  for (const s of sorted) {
    const last = out[out.length - 1];
    if (last && new Date(last.to).getTime() >= new Date(s.from).getTime()) {
      if (new Date(s.to).getTime() > new Date(last.to).getTime()) last.to = s.to;
    } else {
      out.push({ from: s.from, to: s.to, minutes: 0 });
    }
  }
  for (const w of out) w.minutes = Math.round((new Date(w.to).getTime() - new Date(w.from).getTime()) / 60_000);
  return out.sort((a, b) => b.minutes - a.minutes || a.from.localeCompare(b.from));
}

const HOLD_MINUTES = 30;

const slotDay = (iso: string) =>
  new Date(iso).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'short' });

const lasting = (m: number) => (m < 60 ? `${m} min` : m % 60 === 0 ? `${m / 60} h` : `${Math.floor(m / 60)} h ${m % 60}`);

function Slots() {
  const title = 'Your next open slots';
  const navigate = useNavigate();
  // The day, not the instant: the query is keyed by date and should not refetch every render.
  const anchor = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const slots = useSlots(anchor, HOLD_MINUTES, true);
  const availability = useAvailability();
  const who = useWho();
  const me = useMeOrThrow();
  const [chosen, setChosen] = useState<Stretch | null>(null);
  if (slots.isPending) return <Page title={title}><Skeleton rows={4} /></Page>;
  if (slots.isError) return <Page title={title}><Refusal failure={failureOf(slots.error)} /></Page>;

  const windows = windowsOf(slots.data.slots);
  if (windows.length === 0) {
    const noHours = availability.data?.rules.length === 0;
    return (
      <Page title={title}>
        <EmptyState
          title={noHours ? 'You have not said when you can be booked' : 'Nothing open this week'}
          body={
            noHours
              ? 'A free calendar is not a willing one. Say which hours you can be booked and the open ones show here.'
              : `No ${HOLD_MINUTES}-minute stretch is free inside your bookable hours for the rest of this week.`
          }
          action={{ label: 'Set your hours', onClick: () => navigate('/settings/availability') }}
        />
      </Page>
    );
  }

  const longest = windows[0]!.minutes;
  return (
    <View
      title={title}
      lead={`${plural(windows.length, 'free stretch', 'free stretches')} this week, longest first. Hold one and your twin writes it on your calendar with nobody else on it.`}
      rows={windows.map((w) => ({
        id: w.from,
        explain: explainStretch(w, longest, who, viewerOf(me)),
        label: `${slotDay(w.from)}, ${clock(w.from)}–${clock(w.to)}`,
        pct: Math.max(2, Math.round((w.minutes / longest) * 100)),
        bar: 'accent' as const,
        value: lasting(w.minutes),
        action: { label: chosen?.from === w.from ? 'Chosen' : 'Hold it', onClick: () => setChosen(w) },
      }))}
      footer={[{ label: 'Set your hours', onClick: () => navigate('/settings/availability') }]}
      notes={[
        'Only inside the hours you said you can be booked.',
        `A hold takes the first ${HOLD_MINUTES} minutes of the stretch.`,
      ]}
    >
      {chosen && <HoldForm key={chosen.from} stretch={chosen} onDone={() => setChosen(null)} />}
    </View>
  );
}

/**
 * The same hold the calendar makes (CalendarOverlay): a title and who it is
 * with, then the twin writes it; the people on it are told only by the
 * person's own "Send invitations" (ADR-0093). The server refuses a slot a
 * colleague is busy in, in words.
 */
function HoldForm({ stretch: w, onDone }: { stretch: Stretch; onDone: () => void }) {
  const hold = useHold();
  const toast = useToast();
  const [what, setWhat] = useState('');
  const [attendees, setAttendees] = useState<HoldAttendees>(NOBODY);
  const [withPeople, setWithPeople] = useState(false);
  const [held, setHeld] = useState<HoldResult | null>(null);
  const from = w.from;
  const to = new Date(new Date(w.from).getTime() + HOLD_MINUTES * 60_000).toISOString();
  if (held) return <InviteStep held={held} onDone={onDone} />;
  return (
    <div className="flex flex-col gap-2" data-testid="hold-form">
      <div className="flex flex-wrap items-end gap-2">
        <label className="flex min-w-0 flex-1 basis-[240px] flex-col gap-1">
          <span className="text-micro font-medium uppercase tracking-label text-meta">
            What {slotDay(from)}, {clock(from)}–{clock(to)} is for
          </span>
          <TextInput value={what} onChange={(e) => setWhat(e.target.value)} aria-label="What it is for" placeholder="Call ICA about the listing" />
        </label>
        <Button
          variant="primary"
          disabled={hold.isPending || what.trim().length < 2}
          onClick={() =>
            hold.mutate(
              { from, to, title: what.trim(), attendees },
              {
                onSuccess: (r) => {
                  if ((r.attendees ?? []).length > 0) {
                    setHeld(r);
                    return;
                  }
                  toast({ text: `${twinGiven(r.heldBy)}, your twin, is holding ${clock(from)}–${clock(to)} for you. Nobody else is on it.` });
                  onDone();
                },
              },
            )
          }
        >
          Hold {clock(from)}–{clock(to)}
        </Button>
        <Button variant="tertiary" onClick={onDone}>
          Not this one
        </Button>
      </div>
      {withPeople ? (
        <AttendeePicker value={attendees} onChange={setAttendees} />
      ) : (
        <TextAction className="self-start text-dense text-accent underline underline-offset-[3px]" onClick={() => setWithPeople(true)}>
          With somebody?
        </TextAction>
      )}
      {hold.isError && <Refusal compact failure={failureOf(hold.error)} />}
    </div>
  );
}

// --- unknown ----------------------------------------------------------------------

function Unknown({ asked }: { asked: string }) {
  return (
    <Page title="Not an insight">
      <EmptyState
        title="There is no insight by that name"
        body={`“${asked}” is not one of them. These are the ones there are:`}
      />
      <ul aria-label="Insights" className="m-0 flex list-none flex-col gap-px border border-line bg-line p-0">
        {INSIGHTS.map((i) => (
          <li key={i.key}>
            <Link to={`/insight/${i.key}`} className="flex min-h-tap items-center bg-page px-4 text-dense text-ink hover:bg-raised">
              {i.title}
            </Link>
          </li>
        ))}
      </ul>
    </Page>
  );
}
